import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FileText, Upload, Trash2, ImageIcon, LayoutTemplate, Layers } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import OverlayDesigner from './OverlayDesigner';

// 8MB upload cap matches backend LETTERHEAD_MAX_BYTES
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const stripDataUri = (s) => (s || '').includes(',') ? s.split(',')[1] : (s || '');

const LetterheadManager = ({ open, onClose, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [previewBase64, setPreviewBase64] = useState('');
  const [sourceType, setSourceType] = useState('image');
  const [templateMode, setTemplateMode] = useState('header'); // 'header' or 'overlay'
  const [designingId, setDesigningId] = useState(null);       // letterhead id whose designer is open
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/letterheads`, { headers: authHeaders() });
      setItems(data);
    } catch (err) {
      toast.error(`Failed to load letterheads: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB)`);
      return;
    }
    setUploading(true);
    try {
      const dataUri = await fileToBase64(file);
      const raw = stripDataUri(dataUri);
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        // Convert first page server-side to PNG
        const { data } = await axios.post(
          `${API}/api/letterheads/convert-pdf`,
          { pdf_base64: raw },
          { headers: authHeaders() },
        );
        setPreviewBase64(data.image_base64);
        setSourceType('pdf');
        toast.success('PDF first page converted to image');
      } else if (file.type.startsWith('image/')) {
        setPreviewBase64(raw);
        setSourceType('image');
        toast.success('Image loaded');
      } else {
        toast.error('Only PDF or image files are supported');
      }
      // Auto-derive name from filename if empty
      if (!name) {
        const stem = file.name.replace(/\.[^.]+$/, '');
        setName(stem);
      }
    } catch (err) {
      toast.error(`Upload failed: ${apiErr(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!previewBase64) {
      toast.error('Please select a file first');
      return;
    }
    setUploading(true);
    try {
      const created = await axios.post(
        `${API}/api/letterheads`,
        { name: name.trim(), image_base64: previewBase64, source_type: sourceType, template_mode: templateMode },
        { headers: authHeaders() },
      );
      toast.success(`Letterhead "${name.trim()}" saved`);
      const savedId = created?.data?.id;
      setName('');
      setPreviewBase64('');
      setSourceType('image');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
      onChanged?.();
      // If overlay mode, open the designer right away so admin can place fields
      if (templateMode === 'overlay' && savedId) {
        setDesigningId(savedId);
      }
      setTemplateMode('header');
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (lh) => {
    const ok = window.confirm(`Delete letterhead "${lh.name}"?\n\nEstimates/invoices using it will revert to the default branded letterhead.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/letterheads/${lh.id}`, { headers: authHeaders() });
      toast.success('Letterhead deleted');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="letterhead-manager-title" className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-400" /> Letterheads
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Upload a PDF or image to use as a custom letterhead on estimates and invoices. The first page of a PDF is auto-converted to an image.
          </DialogDescription>
        </DialogHeader>

        {/* ===== Upload form ===== */}
        <div className="rounded border border-slate-700 bg-slate-950/40 p-4 space-y-3">
          <Label className="text-gray-300 text-xs">Add new letterhead</Label>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              data-testid="letterhead-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
            />
            <Button
              type="button"
              data-testid="letterhead-pick-file"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border-slate-700 text-white hover:bg-slate-800"
            >
              <Upload className="w-3 h-3 mr-2" /> {uploading ? 'Loading…' : 'Choose PDF or image'}
            </Button>
            <Input
              data-testid="letterhead-name"
              placeholder="Letterhead name (e.g. Main letterhead)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white flex-1 min-w-[200px]"
            />
            <Button
              type="button"
              data-testid="letterhead-save"
              onClick={save}
              disabled={uploading || !previewBase64 || !name.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Save
            </Button>
          </div>
          {previewBase64 && (
            <div className="mt-2 space-y-2">
              <div>
                <p className="text-[11px] text-gray-500 mb-1">Choose how this template is used:</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="letterhead-mode-header"
                    onClick={() => setTemplateMode('header')}
                    className={`flex-1 rounded border px-3 py-2 text-left transition ${templateMode === 'header' ? 'border-amber-400 bg-amber-500/10' : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'}`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <FileText className={`w-3.5 h-3.5 ${templateMode === 'header' ? 'text-amber-400' : 'text-gray-500'}`} />
                      <span className={`text-xs font-semibold ${templateMode === 'header' ? 'text-amber-200' : 'text-gray-300'}`}>Header banner</span>
                    </div>
                    <p className="text-[10px] text-gray-500">Show at the top of the estimate / invoice. Standard layout below.</p>
                  </button>
                  <button
                    type="button"
                    data-testid="letterhead-mode-overlay"
                    onClick={() => setTemplateMode('overlay')}
                    className={`flex-1 rounded border px-3 py-2 text-left transition ${templateMode === 'overlay' ? 'border-amber-400 bg-amber-500/10' : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'}`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <Layers className={`w-3.5 h-3.5 ${templateMode === 'overlay' ? 'text-amber-400' : 'text-gray-500'}`} />
                      <span className={`text-xs font-semibold ${templateMode === 'overlay' ? 'text-amber-200' : 'text-gray-300'}`}>Full overlay template</span>
                    </div>
                    <p className="text-[10px] text-gray-500">Use the whole page as your form. You&apos;ll place fields onto it after saving.</p>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 mb-1">Preview:</p>
                <img
                  src={`data:image/png;base64,${previewBase64}`}
                  alt="Letterhead preview"
                  data-testid="letterhead-preview"
                  className="max-h-48 w-auto border border-slate-700 rounded bg-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* ===== Saved letterheads list ===== */}
        <div className="mt-2">
          <Label className="text-gray-300 text-xs">Saved letterheads ({items.length})</Label>
          {loading ? (
            <p className="text-gray-400 mt-2" data-testid="letterheads-loading">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-gray-500 mt-2 text-sm" data-testid="letterheads-empty">No letterheads yet. Upload one above to get started.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {items.map((lh) => (
                <li
                  key={lh.id}
                  data-testid={`letterhead-row-${lh.id}`}
                  className="flex items-center justify-between rounded border border-slate-800 bg-slate-950/40 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-white text-sm font-medium flex items-center gap-2">
                        {lh.name}
                        {lh.template_mode === 'overlay' && (
                          <span className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
                            Overlay · {(lh.fields || []).length} field{(lh.fields || []).length === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{lh.source_type}{lh.template_mode === 'header' ? ' · header' : ''}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {lh.template_mode === 'overlay' && (
                      <Button
                        size="sm" variant="outline"
                        data-testid={`letterhead-design-${lh.id}`}
                        onClick={() => setDesigningId(lh.id)}
                        title="Open template designer"
                        className="border-slate-700 text-amber-300 hover:bg-amber-950/30 px-2"
                      >
                        <LayoutTemplate className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      data-testid={`letterhead-delete-${lh.id}`}
                      onClick={() => remove(lh)}
                      aria-label="Delete letterhead"
                      className="border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 px-2"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
      <OverlayDesigner
        open={Boolean(designingId)}
        onClose={() => setDesigningId(null)}
        letterheadId={designingId}
        onSaved={() => { load(); onChanged?.(); }}
      />
    </Dialog>
  );
};

export default LetterheadManager;
